import React, { useState, useEffect } from 'react';
import { useApi } from '../hooks/useApi';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';

const OrdersPage = () => {
  const api = useApi();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchOrders = async () => {
      try {
        const response = await api.get('/orders');
        setOrders(response.data);
      } catch (error) {
        console.error('Error fetching orders', error);
      } finally {
        setLoading(false);
      }
    };
    fetchOrders();
  }, [api]);

  if (loading) {
    return (
      <div className="min-h-screen bg-deepDark flex flex-col items-center justify-center">
        <div className="w-8 h-8 border-2 border-gold border-t-transparent rounded-full animate-spin mb-4"></div>
        <p className="text-textMuted text-xs tracking-widest uppercase">Retrieving Archive...</p>
      </div>
    );
  }

  if (orders.length === 0) {
    return (
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="min-h-[80vh] bg-deepDark flex items-center justify-center px-4">
        <div className="text-center p-12 max-w-lg w-full border border-white/5 bg-surfaceDark/50">
          <h2 className="text-3xl font-serif text-white mb-6 tracking-wide">Archive Empty</h2>
          <p className="text-textMuted mb-10 text-sm font-light tracking-wide">You have not yet made any acquisitions.</p>
          <div className="w-16 h-[1px] bg-gold mx-auto mb-10"></div>
          <Link to="/products" className="inline-block border border-gold text-gold px-12 py-4 text-xs font-bold tracking-widest uppercase hover:bg-gold hover:text-deepDark transition-colors">
            Explore Collection
          </Link>
        </div>
      </motion.div>
    );
  }

  const getStatusStyle = (status) => {
    const s = status.toLowerCase();
    if (['delivered'].includes(s)) return 'text-gold border-gold/30 bg-gold/5';
    if (['cancelled'].includes(s)) return 'text-red-500 border-red-500/30 bg-red-500/5';
    return 'text-white border-white/20 bg-transparent';
  };

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.8 }} className="min-h-screen bg-deepDark py-16 px-4">
      <div className="max-w-4xl mx-auto">
        <div className="mb-12 border-b border-white/10 pb-6 flex items-baseline justify-between">
          <h1 className="text-4xl font-serif text-white tracking-wide">Acquisition Archive</h1>
          <span className="text-textMuted text-xs tracking-widest uppercase">{orders.length} Records</span>
        </div>

        <div className="space-y-6">
          {orders.map((order, idx) => (
            <motion.div 
              key={order._id} 
              initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: idx * 0.1 }}
              className="bg-surfaceDark border border-white/5 p-8 group hover:border-gold/30 transition-colors"
            >
              <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 gap-4 border-b border-white/5 pb-6">
                <div>
                  <p className="text-[10px] tracking-widest uppercase text-textMuted mb-1">Reference No.</p>
                  <h3 className="text-xl font-serif text-white tracking-wider">{order.orderNumber}</h3>
                </div>
                <div className="text-left sm:text-right">
                  <p className="text-[10px] tracking-widest uppercase text-textMuted mb-1">{new Date(order.createdAt).toLocaleDateString()}</p>
                  <span className={`inline-block px-3 py-1 border text-[10px] tracking-widest uppercase font-bold ${getStatusStyle(order.status)}`}>
                    {order.status}
                  </span>
                </div>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-8">
                <div>
                  <p className="text-[10px] tracking-widest uppercase text-textMuted mb-1">Items</p>
                  <p className="text-lg font-serif text-white">{order.items.length}</p>
                </div>
                <div>
                  <p className="text-[10px] tracking-widest uppercase text-textMuted mb-1">Total</p>
                  <p className="text-lg font-serif text-gold">₹{order.totalAmount.toLocaleString()}</p>
                </div>
                <div className="col-span-2 md:col-span-2">
                  <p className="text-[10px] tracking-widest uppercase text-textMuted mb-1">Method</p>
                  <p className="text-sm font-light text-white uppercase tracking-wider">{order.paymentMethod}</p>
                </div>
              </div>

              <Link 
                to={`/order/${order._id}`}
                className="inline-block text-[10px] tracking-widest uppercase text-gold hover:text-white border-b border-gold hover:border-white pb-1 transition-all"
              >
                View Documentation & Tracking
              </Link>
            </motion.div>
          ))}
        </div>
      </div>
    </motion.div>
  );
};

export default OrdersPage;
